// src/pages/legal/TermsOfService.tsx
import React from 'react';
import HomeLayout from '../../hocs/layout/HomeLayout';
import { Meta } from '../../components/navigation/Helmet';

function TermsOfService() {
  return (
    <HomeLayout>
      <Meta title="Términos y Condiciones" />
      
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Términos y Condiciones de Uso & EULA
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-6 text-center">
                <strong>Última actualización:</strong> 18 de julio de 2025
              </p>

              {/* 1. Introducción */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introducción</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Bienvenido a <strong>TableChat</strong>, una plataforma que permite realizar pedidos y pagos
                  directamente desde su mesa en restaurantes, sin esperar al mesero. Al usar nuestra aplicación
                  o sitio web, usted acepta estos Términos y Condiciones de Uso y el Acuerdo de Licencia de
                  Usuario Final (EULA). Si no está de acuerdo, no utilice TableChat.
                </p>
                <p className="text-gray-700">
                  TableChat es desarrollada y operada por <strong>Ovalcampus</strong>, con presencia inicial en
                  Colombia y operaciones globales.
                </p>
              </section>

              {/* 2. Definiciones */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Definiciones</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Usuario:</strong> Persona que crea una cuenta o utiliza TableChat.</li>
                  <li><strong>Restaurante Socio:</strong> Establecimiento que recibe y gestiona pedidos vía TableChat.</li>
                  <li><strong>Pedido:</strong> Solicitud de productos/servicios realizada por el Usuario al Restaurante Socio.</li>
                  <li><strong>Suscripción/IAP:</strong> Compra dentro de la app (In-App Purchase) o plan auto-renovable que habilita funciones o beneficios.</li>
                  <li><strong>EULA de Apple:</strong> Acuerdo estándar de Apple para apps iOS, disponible en 
                    {' '}<a className="text-blue-600 underline" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">
                      este enlace oficial
                    </a>.
                  </li>
                </ul>
              </section>

              {/* 3. Cuenta y seguridad */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Registro y Seguridad de la Cuenta</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Debe proporcionar información veraz y mantenerla actualizada.</li>
                  <li>Usted es responsable de la confidencialidad de sus credenciales y de toda actividad realizada con su cuenta.</li>
                  <li>Debe ser mayor de 18 años o contar con autorización parental conforme a la ley aplicable.</li>
                  <li>Podemos suspender o cerrar cuentas ante incumplimientos, fraude o riesgos de seguridad.</li>
                </ul>
              </section>

              {/* 4. Uso del servicio */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Uso de la Plataforma</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Uso permitido</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Realizar pedidos de alimentos y bebidas a Restaurantes Socios.</li>
                  <li>Consultar menús, disponibilidad y precios.</li>
                  <li>Realizar pagos seguros dentro de la plataforma.</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Uso prohibido</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Pedidos fraudulentos, maliciosos o con intención de perturbar la operación del restaurante.</li>
                  <li>Intentar acceder o interferir con sistemas no autorizados.</li>
                  <li>Publicar contenido ilegal, ofensivo o que infrinja derechos de terceros.</li>
                </ul>
              </section>

              {/* 5. Pedidos y pagos */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Pedidos, Precios y Pagos</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Los precios, la disponibilidad y los tiempos de preparación dependen del Restaurante Socio.</li>
                  <li>El pedido queda confirmado cuando el restaurante lo acepta; los tiempos mostrados son estimados.</li>
                  <li>Los pagos se procesan mediante proveedores certificados. Pueden aplicarse impuestos, tasas y/o propinas.</li>
                  <li>TableChat facilita la transacción tecnológica, pero no prepara ni entrega los productos.</li>
                </ul>
              </section>

              {/* 6. Suscripciones e IAP (Apple/Google) */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Suscripciones y Compras Dentro de la App (IAP)</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Cuando existan suscripciones o IAP, los precios, beneficios y períodos se muestran antes del pago.</li>
                  <li>Las suscripciones son <strong>auto-renovables</strong> a menos que se cancelen con al menos 24 horas de
                      antelación al final del período actual.</li>
                  <li>Puede gestionar o cancelar su suscripción desde la configuración de su cuenta en App Store/Google Play.</li>
                  <li>La opción <em>“Restaurar Compras”</em> estará disponible cuando aplique (requerimiento Apple).</li>
                  <li>Para iOS, además de estos términos, su uso está sujeto al 
                    {' '}<a className="text-blue-600 underline" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">
                      Apple Standard EULA
                    </a>.
                  </li>
                  <li><strong>Apple y Google no son parte</strong> de este contrato entre usted y Ovalcampus respecto de TableChat; no son responsables del soporte o mantenimiento del servicio.</li>
                </ul>
              </section>

              {/* 7. Cancelaciones y reembolsos */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Cancelaciones y Reembolsos</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Pedidos: la política de cancelaciones y reembolsos depende del Restaurante Socio y la legislación aplicable.</li>
                  <li>Suscripciones/IAP: siguen las reglas de la tienda (App Store/Google Play) y los lineamientos exhibidos en el flujo de compra.</li>
                </ul>
              </section>

              {/* 8. Propiedad intelectual */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Propiedad Intelectual</h2>
                <p className="text-gray-700">
                  TableChat, su software, marcas, diseños y contenidos son propiedad de Ovalcampus o sus licenciantes.
                  Usted recibe una licencia limitada, personal, no exclusiva e intransferible para usar la app con el
                  único fin de realizar pedidos y pagos en restaurantes.
                </p>
              </section>

              {/* 9. Garantías y responsabilidad */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Garantías y Limitación de Responsabilidad</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>El servicio se ofrece “tal cual” y “según disponibilidad”. No garantizamos disponibilidad ininterrumpida ni ausencia total de errores.</li>
                  <li>Ovalcampus no es responsable por la calidad, disponibilidad o entrega de productos de los Restaurantes Socios.</li>
                  <li>Nuestra responsabilidad se limita al máximo permitido por la ley aplicable.</li>
                </ul>
              </section>

              {/* 10. Ley aplicable y disputas */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Ley Aplicable y Resolución de Disputas</h2>
                <p className="text-gray-700">
                  Estos términos se interpretan conforme a la ley imperativa aplicable en su jurisdicción. Cualquier
                  disputa se resolverá preferiblemente por negociación; de no ser posible, mediante los mecanismos de
                  resolución previstos por la ley (incluido arbitraje o jurisdicción competente, según corresponda).
                </p>
              </section>

              {/* 11. Cambios */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Cambios a estos Términos</h2>
                <p className="text-gray-700">
                  Podemos actualizar estos Términos/EULA para reflejar cambios en el servicio o en requisitos legales.
                  Le notificaremos cambios sustanciales dentro de la app o por correo electrónico.
                </p>
              </section>

              {/* 12. Contacto */}
              <section className="mb-2">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Contacto</h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email legal:</strong> legal@tablechat.com</p>
                  <p className="text-gray-700"><strong>Dirección (Ovalcampus USA):</strong><br/>
                    1129 N Saint Lucas St<br/>
                    Allentown, PA 18104, Estados Unidos
                  </p>
                </div>
                <p className="text-gray-700 mt-4">
                  Para iOS, también aplica el EULA de Apple:{" "}
                  <a className="text-blue-600 underline" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">
                    https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
                  </a>
                </p>
              </section>

              <div className="border-t border-gray-200 pt-8 mt-8 text-center">
                <p className="text-gray-600">
                  Al utilizar TableChat, usted confirma que ha leído, entendido y acepta estos Términos/EULA.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
}

export default TermsOfService;
